import { useCallback, useEffect, useState } from "react";
import { onAuthStateChanged, onIdTokenChanged, signInWithPopup, signOut as firebaseSignOut } from "firebase/auth";
import { auth, googleProvider } from "./firebaseClient";
import { USE_FIREBASE_AUTH } from "./config";

export function useAuth() {
  const [user, setUser] = useState(null);
  const [idToken, setIdToken] = useState(null);
  const [loading, setLoading] = useState(USE_FIREBASE_AUTH);

  useEffect(() => {
    if (!USE_FIREBASE_AUTH) {
      setLoading(false);
      return undefined;
    }

    const unsubAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        setIdToken(await firebaseUser.getIdToken());
      } else {
        setIdToken(null);
      }
      setLoading(false);
    });

    const unsubToken = onIdTokenChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setIdToken(await firebaseUser.getIdToken());
      }
    });

    return () => {
      unsubAuth();
      unsubToken();
    };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    if (!USE_FIREBASE_AUTH) throw new Error("Firebase Auth is not configured");
    const result = await signInWithPopup(auth, googleProvider);
    const token = await result.user.getIdToken();
    setIdToken(token);
    return result.user;
  }, []);

  const signOut = useCallback(async () => {
    if (USE_FIREBASE_AUTH) await firebaseSignOut(auth);
    setUser(null);
    setIdToken(null);
  }, []);

  const getToken = useCallback(async () => {
    if (!auth?.currentUser) return null;
    const token = await auth.currentUser.getIdToken();
    setIdToken(token);
    return token;
  }, []);

  return { user, idToken, loading, signInWithGoogle, signOut, getToken };
}
